<template>
  <div ref="rootRef" class="move-global-search" :class="{ 'is-open': isOpen, 'is-expanded': isExpanded }">
    <button
      v-if="isMobile && !isExpanded"
      type="button"
      class="move-theme-toggle !w-9 !h-9"
      aria-label="Open search"
      @click="openMobileSearch"
    >
      <span class="material-icons text-lg">search</span>
    </button>

    <div v-show="!isMobile || isExpanded" class="move-global-search__field">
      <span class="material-icons move-global-search__icon">search</span>
      <input
        ref="inputRef"
        v-model="query"
        type="search"
        class="move-global-search__input"
        :placeholder="isMobile ? 'Search boards, tasks…' : 'Search boards, tasks, people…'"
        aria-label="Global search"
        autocomplete="off"
        autocapitalize="off"
        spellcheck="false"
        @focus="handleFocus"
        @input="handleInput"
        @keydown="handleKeydown"
      />
      <button
        v-if="isMobile && isExpanded"
        type="button"
        class="move-global-search__close"
        aria-label="Close search"
        @click="closeSearch"
      >
        <span class="material-icons text-lg">close</span>
      </button>
      <kbd v-else-if="!isMobile" class="move-global-search__kbd" aria-hidden="true">⌘K</kbd>
    </div>

    <transition name="fade-slide">
      <div
        v-if="isOpen && (query.trim() || loading)"
        class="move-global-search__panel"
        role="listbox"
        aria-label="Search results"
      >
        <div v-if="loading" class="move-global-search__empty">
          <span class="material-icons animate-spin text-base">sync</span>
          Updating index…
        </div>

        <template v-else-if="query.trim()">
          <template v-if="results.total > 0">
            <section v-if="results.boards.length" class="move-global-search__group">
              <h3 class="move-global-search__group-title">Boards</h3>
              <button
                v-for="(item, index) in results.boards"
                :key="`board-${item.id}`"
                type="button"
                class="move-global-search__item"
                :class="{ 'is-active': activeIndex === indexOffset.boards + index }"
                role="option"
                @mouseenter="activeIndex = indexOffset.boards + index"
                @click="goTo(item)"
              >
                <span class="material-icons">{{ item.icon }}</span>
                <span class="min-w-0">
                  <span class="move-global-search__item-title">{{ item.title }}</span>
                  <span class="move-global-search__item-sub">{{ item.subtitle }}</span>
                </span>
              </button>
            </section>

            <section v-if="results.tasks.length" class="move-global-search__group">
              <h3 class="move-global-search__group-title">Tasks</h3>
              <button
                v-for="(item, index) in results.tasks"
                :key="`task-${item.id}`"
                type="button"
                class="move-global-search__item"
                :class="{ 'is-active': activeIndex === indexOffset.tasks + index }"
                role="option"
                @mouseenter="activeIndex = indexOffset.tasks + index"
                @click="goTo(item)"
              >
                <span class="material-icons">{{ item.icon }}</span>
                <span class="min-w-0">
                  <span class="move-global-search__item-title">{{ item.title }}</span>
                  <span class="move-global-search__item-sub">{{ item.subtitle }}</span>
                </span>
              </button>
            </section>

            <section v-if="results.users.length" class="move-global-search__group">
              <h3 class="move-global-search__group-title">People</h3>
              <button
                v-for="(item, index) in results.users"
                :key="`user-${item.id}`"
                type="button"
                class="move-global-search__item"
                :class="{ 'is-active': activeIndex === indexOffset.users + index }"
                role="option"
                @mouseenter="activeIndex = indexOffset.users + index"
                @click="goTo(item)"
              >
                <span class="material-icons">{{ item.icon }}</span>
                <span class="min-w-0">
                  <span class="move-global-search__item-title">{{ item.title }}</span>
                  <span class="move-global-search__item-sub">{{ item.subtitle }}</span>
                </span>
              </button>
            </section>
          </template>

          <div v-else class="move-global-search__empty">
            <span class="material-icons text-base">search_off</span>
            No results for “{{ query.trim() }}”
          </div>
        </template>
      </div>
    </transition>
  </div>
</template>

<script setup>
/* global defineProps, defineExpose */
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useGlobalSearch } from '../composables/useGlobalSearch'

const props = defineProps({
  isAdmin: {
    type: Boolean,
    default: false
  }
})

const router = useRouter()
const { loadIndex, search, flatResults, loading, invalidateIndex } = useGlobalSearch()

const rootRef = ref(null)
const inputRef = ref(null)
const query = ref('')
const isOpen = ref(false)
const isExpanded = ref(false)
const isMobile = ref(window.innerWidth < 768)
const activeIndex = ref(0)

const results = computed(() => search(query.value, { isAdmin: props.isAdmin }))

const indexOffset = computed(() => ({
  boards: 0,
  tasks: results.value.boards.length,
  users: results.value.boards.length + results.value.tasks.length
}))

const allResults = computed(() => flatResults(query.value, { isAdmin: props.isAdmin }))

watch(query, () => {
  activeIndex.value = 0
  isOpen.value = true
})

watch(allResults, (items) => {
  if (activeIndex.value >= items.length) {
    activeIndex.value = Math.max(0, items.length - 1)
  }
})

const handleFocus = async () => {
  isOpen.value = true
  await loadIndex(props.isAdmin)
}

const handleInput = async () => {
  isOpen.value = true
  if (!loading.value) await loadIndex(props.isAdmin)
}

const openMobileSearch = async () => {
  isExpanded.value = true
  isOpen.value = true
  await loadIndex(props.isAdmin)
  await nextTick()
  inputRef.value?.focus()
}

const closeSearch = () => {
  isExpanded.value = false
  isOpen.value = false
  query.value = ''
  activeIndex.value = 0
}

const goTo = (item) => {
  if (!item?.to) return
  closeSearch()
  router.push(item.to)
}

const handleKeydown = (event) => {
  const items = allResults.value

  if (event.key === 'ArrowDown') {
    event.preventDefault()
    if (!items.length) return
    activeIndex.value = (activeIndex.value + 1) % items.length
  } else if (event.key === 'ArrowUp') {
    event.preventDefault()
    if (!items.length) return
    activeIndex.value = (activeIndex.value - 1 + items.length) % items.length
  } else if (event.key === 'Enter') {
    event.preventDefault()
    const item = items[activeIndex.value]
    if (item) goTo(item)
  } else if (event.key === 'Escape') {
    event.preventDefault()
    closeSearch()
    inputRef.value?.blur()
  }
}

const handleGlobalKeydown = (event) => {
  const isShortcut = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k'
  if (!isShortcut) return

  event.preventDefault()
  if (isMobile.value) {
    openMobileSearch()
  } else {
    isOpen.value = true
    loadIndex(props.isAdmin)
    nextTick(() => inputRef.value?.focus())
  }
}

const handleClickOutside = (event) => {
  if (!rootRef.value?.contains(event.target)) {
    isOpen.value = false
    if (isMobile.value) isExpanded.value = false
  }
}

const handleResize = () => {
  isMobile.value = window.innerWidth < 768
  if (!isMobile.value) isExpanded.value = false
}

onMounted(() => {
  window.addEventListener('keydown', handleGlobalKeydown)
  document.addEventListener('click', handleClickOutside)
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleGlobalKeydown)
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('resize', handleResize)
})

defineExpose({ invalidateIndex })
</script>
